import { useEffect, useState } from "react";
import { LuCircleCheck, LuClock, LuLoaderCircle } from "react-icons/lu";
import List from "../List/List";
import Pagination from "../Pagination/Pagination";
import LicenseService from "../../services/LicensesService";

const licenseActionNames = {
   ACTIVATE: "Ativação",
   BLOCK: "Bloqueio",
   UNBLOCK: "Desbloqueio",
   SUSPEND: "Suspensão",
   CANCEL: "Cancelamento",
   Delete: "Exclusão",
   CHANGE_FEATURES: "Alteração de funcionalidades",
};

const actionStatusPresentation = {
   scheduled: {
      label: "Agendado",
      Icon: LuClock,
      className: "text-amber-500 bg-amber-500/10",
   },
   pending: {
      label: "Pendente",
      Icon: LuClock,
      className: "text-gray-500 bg-gray-500/10",
   },
   in_progress: {
      label: "Em andamento",
      Icon: LuLoaderCircle,
      className: "text-blue-500 bg-blue-500/10",
   },
   completed: {
      label: "Concluído",
      Icon: LuCircleCheck,
      className: "text-green-500 bg-green-500/10",
   },
};

function getActionStatus(action) {
   if (action.executedAt) return "completed";
   if (action.startedAt) return "in_progress";
   if (action.scheduledFor && new Date(action.scheduledFor) > new Date()) {
      return "scheduled";
   }
   return "pending";
}

export function LicenseSchedulingPage({ license }) {
   const [actions, setActions] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [error, setError] = useState("");
   const [page, setPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(6);
   const [pagination, setPagination] = useState({
      currentPage: 1,
      totalItems: 0,
      totalPages: 0,
   });

   useEffect(() => {
      let isCurrentRequest = true;

      async function fetchActions() {
         setIsLoading(true);
         setError("");

         const response = await LicenseService.getPaginatedLicenseActions(
            license.id,
            page,
            itemsPerPage,
         );

         if (!isCurrentRequest) return;

         if (response?.status >= 200 && response.status < 300) {
            const result = response.data;
            const pageInfo = result.pagination ?? {};
            setActions(result.items ?? []);
            setPagination({
               currentPage: pageInfo.currentPage ?? page,
               totalItems: pageInfo.totalItems ?? 0,
               totalPages: pageInfo.totalPages ?? 0,
            });
         } else {
            setActions([]);
            setPagination({ currentPage: page, totalItems: 0, totalPages: 0 });
            setError(
               response?.data?.message ??
               "Não foi possível carregar os agendamentos desta licença.",
            );
         }

         setIsLoading(false);
      }

      fetchActions();
      return () => {
         isCurrentRequest = false;
      };
   }, [license.id, page, itemsPerPage]);

   const columns = [
      {
         forceLabel: true,
         collumnLabel: "Ação",
         index: "type",
         type: "custom",
         custom: ({ row }) => licenseActionNames[row.type] ?? row.type,
      },
      { forceLabel: true, collumnLabel: "Agendado para", index: "scheduledFor", type: "datetime" },
      { forceLabel: true, collumnLabel: "Motivo", index: "reason", type: "text" },
      {
         forceLabel: true,
         collumnLabel: "Status",
         index: "status",
         type: "custom",
         custom: ({ row }) => {
            const { label, Icon, className } = actionStatusPresentation[getActionStatus(row)];
            return (
               <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5! py-1! text-xs! font-semibold! ${className}`}>
                  <Icon size={14} />
                  {label}
               </span>
            );
         },
      },
      { forceLabel: true, collumnLabel: "Solicitado em", index: "createdAt", type: "datetime" },
      { forceLabel: true, collumnLabel: "Solicitado por", index: "agentData.name", type: "text" },
   ];

   return (
      <div className="fade-in">
         <section>
            <h3 className="font-bold! mb-4!">Agendamentos</h3>
            {error && (
               <p className="mb-3! text-red-500" role="alert">{error}</p>
            )}
            <List
               data={actions}
               collumsObjIndex={columns}
               isLoading={isLoading}
               selectableRows={false}
            />
            <Pagination
               currentPage={pagination.currentPage}
               maxPage={pagination.totalPages}
               rowsPerPage={itemsPerPage}
               total={pagination.totalItems}
               changePage={setPage}
               changeRowsPerPage={(value) => {
                  setPage(1);
                  setItemsPerPage(value);
               }}
            />
         </section>
      </div>
   );
}